import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Store,
  IndianRupee,
  Percent,
  Users,
  Loader2,
  AlertCircle,
  RefreshCw,
  BarChart2,
  TrendingUp,
  ShoppingCart,
  UserPlus,
  Trophy,
  Activity,
} from "lucide-react";
import { getAdminDashboard } from "../store/slices/adminReportSlice";
import useFilterDropdowns from "../hooks/useFilterDropdowns";
import useUrlFilters from "../hooks/useUrlFilters";
import AdminFilterBar from "../components/ui/AdminFilterBar";

// ─── constants ───────────────────────────────────────────────────────────────

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;

const DEFAULT_FILTERS = {
  month: defaultMonth,
  year: defaultYear,
  district_id: "",
  taluka_id: "",
  category_id: "",
  sub_category_id: "",
};

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const num = (v) => (v ?? 0).toLocaleString("en-IN");

// ─── sub-components ──────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, iconBg, textColor }) {
  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm flex items-start space-x-3">
      <div className={`${iconBg} p-2.5 rounded-lg flex-shrink-0`}>
        <Icon className={`h-5 w-5 ${textColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
        <p className={`text-lg font-bold ${textColor} mt-0.5`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dashboardData, dashboardLoading, dashboardError } = useSelector((state) => state.adminReport);

  // ── filters (synced with URL) ──────────────────────────────────────────────
  const { filters, updateFilter, resetFilters } = useUrlFilters(DEFAULT_FILTERS, {
    numberFields: ["month", "year"],
  });

  // ── dropdown data (reusable hook) ──────────────────────────────────────────
  const dropdowns = useFilterDropdowns({
    district_id: filters.district_id,
    category_id: filters.category_id,
  });

  // ── fetch dashboard ────────────────────────────────────────────────────────
  const fetchDashboard = useCallback((currentFilters = filters) => {
    dispatch(getAdminDashboard(currentFilters));
  }, [dispatch, filters]);

  useEffect(() => {
    fetchDashboard(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.month, filters.year, filters.district_id, filters.taluka_id, filters.category_id, filters.sub_category_id]);

  // ── filter actions ─────────────────────────────────────────────────────────
  const handleRefresh = () => fetchDashboard(filters);

  // ── derived data ───────────────────────────────────────────────────────────
  const period = dashboardData?.period || {};
  const summary = dashboardData?.summary || {};
  const liveStats = dashboardData?.live_statistics || {};
  const topBusinesses = dashboardData?.top_performing_businesses || [];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BarChart2 className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-xs sm:text-sm text-gray-500">
                Overview for {period.formatted || `${period.month_name || ""} ${period.year || ""}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={dashboardLoading}
            className="self-end sm:self-auto p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${dashboardLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <AdminFilterBar
          filters={filters}
          onFilterChange={updateFilter}
          onReset={resetFilters}
          dropdowns={dropdowns}
          showFields={["year", "month", "district", "taluka", "category", "sub_category"]}
        />
      </div>

      {/* Content */}
      {dashboardLoading ? (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span>Loading dashboard…</span>
        </div>
      ) : dashboardError ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <AlertCircle className="h-10 w-10 text-red-400" />
          <p className="text-gray-600">Failed to load dashboard data.</p>
          <button onClick={handleRefresh} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
            Try Again
          </button>
        </div>
      ) : dashboardData ? (
        <>
          {/* Live Statistics */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Activity className="h-4 w-4 text-green-500" />
              <h2 className="text-base font-semibold text-gray-900">Live Statistics (Today)</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              <KpiCard icon={ShoppingCart} label="Transactions Today"      value={num(liveStats.transactions_done_today)}          iconBg="bg-blue-100"    textColor="text-blue-600" />
              <KpiCard icon={IndianRupee}  label="Transaction Amount"      value={fmt(liveStats.total_transaction_amount_today)}   iconBg="bg-purple-100"  textColor="text-purple-600" />
              <KpiCard icon={Percent}      label="Commission Today"        value={fmt(liveStats.commission_today)}                 iconBg="bg-orange-100"  textColor="text-orange-600" />
              <KpiCard icon={UserPlus}     label="New Customers"           value={num(liveStats.new_customers_today)}              iconBg="bg-green-100"   textColor="text-green-600" />
              <KpiCard icon={Store}        label="Businesses Joined"       value={num(liveStats.businesses_joined_today)}          iconBg="bg-teal-100"    textColor="text-teal-600" />
            </div>
          </div>

          {/* Monthly Summary */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              Monthly Summary — {period.formatted || `${period.month_name || ""} ${period.year || ""}`}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              <KpiCard icon={Store}        label="Total Businesses"     value={num(summary.total_businesses)}        iconBg="bg-gray-100"    textColor="text-gray-700" />
              <KpiCard icon={TrendingUp}   label="Active Businesses"    value={num(summary.active_businesses)}       iconBg="bg-blue-100"    textColor="text-blue-600" />
              <KpiCard icon={Store}        label="New Businesses"       value={num(summary.new_businesses)}          iconBg="bg-emerald-100" textColor="text-emerald-600" />
              <KpiCard icon={Users}        label="Total Customers"      value={num(summary.total_customers)}         iconBg="bg-indigo-100"  textColor="text-indigo-600" />
              <KpiCard icon={ShoppingCart}  label="Transactions Done"    value={num(summary.total_transactions_done)} iconBg="bg-purple-100"  textColor="text-purple-600" />
              <KpiCard icon={IndianRupee}  label="Total Revenue"        value={fmt(summary.total_revenue)}           iconBg="bg-green-100"   textColor="text-green-600" />
              <KpiCard icon={IndianRupee}  label="Discount Given"       value={fmt(summary.total_discount_given)}    iconBg="bg-yellow-100"  textColor="text-yellow-600" />
              <KpiCard icon={IndianRupee}  label="Connectri Commission" value={fmt(summary.connectri_commission)}    iconBg="bg-orange-100"  textColor="text-orange-600" />
            </div>
          </div>

          {/* Top Performing Businesses */}
          {topBusinesses.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <h2 className="text-base font-semibold text-gray-900">Top Performing Businesses</h2>
                </div>
                <button
                  onClick={() => navigate("/reports")}
                  className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  View All Businesses
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {["#", "Business", "District", "Category", "Revenue", "Transactions", "Commission"].map((col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {topBusinesses.map((biz) => (
                      <tr key={biz.business_uuid} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{biz.rank}</td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900 whitespace-nowrap">{biz.business_name}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                          {biz.district?.name || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                          {biz.category?.name || "—"}
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                          {fmt(biz.total_revenue)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {num(biz.transaction_count)}
                        </td>
                        <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">
                          {fmt(biz.connectri_commission)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <BarChart2 className="h-12 w-12 text-gray-300" />
          <p className="text-gray-500 font-medium">No data available</p>
        </div>
      )}
    </div>
  );
}
